import { useCallback, useLayoutEffect, useRef } from "react";

const extractSnapshot = (file) => ({
  formFields: file?.document?.tempFields?.formFields ?? [],
  selectedFormField: file?.document?.tempFields?.selectedFormField ?? null,
  selectedExtraFields: file?.document?.tempFields?.selectedExtraFields ?? [],
  pdfTextEdits: file?.document?.tempFields?.pdfTextEdits ?? [],
  selectedPdfTextItem: file?.document?.tempFields?.selectedPdfTextItem ?? null,
  selectedExtraPdfTextItems:
    file?.document?.tempFields?.selectedExtraPdfTextItems ?? [],
});

const applySnapshot = (file, snapshot) => {
  if (!file?.document?.tempFields) return file;

  return {
    ...file,
    document: {
      ...file.document,
      tempFields: {
        ...file.document.tempFields,
        formFields: snapshot.formFields,
        selectedFormField: snapshot.selectedFormField,
        selectedExtraFields: snapshot.selectedExtraFields,
        pdfTextEdits: snapshot.pdfTextEdits,
        selectedPdfTextItem: snapshot.selectedPdfTextItem,
        selectedExtraPdfTextItems: snapshot.selectedExtraPdfTextItems,
        // Restore document content/selection, not an active typing session.
        editingInsertedTextId: null,
      },
    },
  };
};

const MAX_HISTORY = 100;

export const useUndoRedo = (
  currentProcessingFile,
  setCurrentProcessingFile,
) => {
  const past = useRef([]);
  const future = useRef([]);

  const currentFileRef = useRef(currentProcessingFile);

  useLayoutEffect(() => {
    currentFileRef.current = currentProcessingFile;
  }, [currentProcessingFile]);

  const pushHistory = useCallback(() => {
    const file = currentFileRef.current;
    if (!file) return;

    const snapshot = extractSnapshot(file);
    past.current = [...past.current.slice(-MAX_HISTORY + 1), snapshot];
    future.current = [];
  }, []);

  const undo = useCallback(() => {
    if (past.current.length === 0) return;

    // Save current state into future
    const currentSnapshot = extractSnapshot(currentFileRef.current);
    future.current = [currentSnapshot, ...future.current];

    // Pop last past state
    const previous = past.current[past.current.length - 1];
    past.current = past.current.slice(0, -1);

    setCurrentProcessingFile((prevFile) => applySnapshot(prevFile, previous));
  }, [setCurrentProcessingFile]);

  const redo = useCallback(() => {
    if (future.current.length === 0) return;

    // Save current state into past
    const currentSnapshot = extractSnapshot(currentFileRef.current);
    past.current = [...past.current, currentSnapshot];

    // Pop first future state
    const next = future.current[0];
    future.current = future.current.slice(1);

    setCurrentProcessingFile((prevFile) => applySnapshot(prevFile, next));
  }, [setCurrentProcessingFile]);

  const canUndo = past.current.length > 0;
  const canRedo = future.current.length > 0;

  return { pushHistory, undo, redo, canUndo, canRedo };
};
